import React, {useMemo} from 'react'
import {Link} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGTM} from '@app/hooks/use-gtm'

// Constants
import {
    SIZE_GUIDE_FOLDER_PATH,
    CODE_MARCHE_FEM,
    CODE_MARCHE_GAR,
    CODE_MARCHE_FIL,
    CODE_MARCHE_HOM,
    CODE_MARCHE_UNISEX_ADULT,
    CODE_FORM_MDB,
    CODE_FORM_PAP,
    CODE_FORM_ACC,
    TYPE_PROD_HAUT,
    TYPE_PROD_BAS,
    LIGNE_PROD_CHA,
    DEFAULT_SIZE_GUIDE_LANGUAGE,
    PDF_EXTENSION
} from '../../constants'

/**
 * SizeGuideTrigger is a React functional component that renders a button linking to a size guide PDF.
 * The PDF is determined based on the product's market code, form code, and type of product.
 * The component also considers the locale to determine the language of the size guide.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.position - The CSS position property for the button.
 * @param {string} props.top - The CSS top property for the button.
 * @param {string} props.right - The CSS right property for the button.
 * @param {Object} props.product - The product object containing details used to determine the size guide type.
 * @param {string} props.product.c_orliweb_stat03_code_marche - The market code of the product.
 * @param {string} props.product.c_orliweb_code_form - The form code of the product.
 * @param {string} props.product.c_orliweb_stat10_type_prod - The type of product (e.g., top, bottom).
 * @param {string} props.product.c_orliweb_code_ligne_produit - The product line code (e.g., shoes).
 *
 * @returns {JSX.Element} A button element that links to the appropriate size guide PDF.
 */
const SizeGuideTrigger = ({position, top, right, product}) => {
    const {locale} = useMultiSite()
    const {triggerClickSizeGuideEvent} = useGTM()

    const {
        c_orliweb_stat03_code_marche: marketCode,
        c_orliweb_code_form: formCode,
        c_orliweb_stat10_type_prod: productType,
        c_orliweb_code_ligne_produit: productLineCode
    } = product

    let language = locale ? locale.id.split('-')[0].toUpperCase() : DEFAULT_SIZE_GUIDE_LANGUAGE
    let productPdfType = ''

    productPdfType = useMemo(() => {
        if (marketCode === CODE_MARCHE_FEM) {
            // Display Women size guide component
            return CODE_MARCHE_FEM
        } else if (marketCode === CODE_MARCHE_GAR) {
            // Display boys size guide component
            return CODE_MARCHE_GAR
        } else if (marketCode === CODE_MARCHE_FIL) {
            // Display girls size guide component
            return CODE_MARCHE_FIL
        } else if (marketCode === CODE_MARCHE_HOM) {
            // Display size guide for Men Swimwear size guide component
            if (formCode === CODE_FORM_MDB) {
                // Men Swimwear
                return `${CODE_FORM_MDB}_${CODE_MARCHE_HOM}`
            } else if (formCode === CODE_FORM_PAP) {
                // Men Ready-to-wear
                if (productType === TYPE_PROD_HAUT) {
                    // Display Men Ready-to-wear top size guide component
                    return `${CODE_FORM_PAP}_${CODE_MARCHE_HOM}_${TYPE_PROD_HAUT}`
                } else if (productType === TYPE_PROD_BAS) {
                    // Display Men Ready-to-wear bottom size guide component
                    return `${CODE_FORM_PAP}_${CODE_MARCHE_HOM}_${TYPE_PROD_BAS}`
                }
            } else if (formCode === CODE_FORM_ACC) {
                // Men Accessories
                if (productLineCode === LIGNE_PROD_CHA) {
                    // Display Men Accessories shoes size guide component
                    return `${CODE_FORM_ACC}_${CODE_MARCHE_HOM}_${LIGNE_PROD_CHA}`
                }
            }
        } else if (marketCode === CODE_MARCHE_UNISEX_ADULT) {
            if (formCode === CODE_FORM_ACC) {
                if (productLineCode === LIGNE_PROD_CHA) {
                    // Display Men Accessories shoes size guide component
                    return `${CODE_FORM_ACC}_${CODE_MARCHE_HOM}_${LIGNE_PROD_CHA}`
                }
            }
        }
    }, [marketCode, formCode, productType, productLineCode])

    const sizeGuideUrl = `${SIZE_GUIDE_FOLDER_PATH}SizeGuide_${productPdfType}_${language}${PDF_EXTENSION}`
    const triggerClickSizeGuide = () => {
        triggerClickSizeGuideEvent()
    }
    return (
        productPdfType && (
            <Link
                href={sizeGuideUrl}
                isExternal
                position={position}
                top={top}
                right={right}
                textDecoration={'underline'}
                onClick={() => triggerClickSizeGuide()}
            >
                <FormattedMessage defaultMessage="Size guide" id="size_guide.pdp.trigger_button" />
            </Link>
        )
    )
}

SizeGuideTrigger.propTypes = {
    position: PropTypes.string,
    top: PropTypes.number,
    right: PropTypes.number,
    product: PropTypes.object.isRequired
}

export default SizeGuideTrigger
